import { create } from "zustand";
import type { ExamDetail, SubmitAnswerRequest } from "@/types";

interface ExamState {
  currentExam: ExamDetail | null;
  currentAttemptId: string | null;
  currentQuestionIndex: number;
  answers: Map<string, string>;
  timeRemaining: number;
  isSubmitting: boolean;

  startExam: (exam: ExamDetail, attemptId: string) => void;
  answerQuestion: (questionId: string, answerId: string) => void;
  nextQuestion: () => void;
  prevQuestion: () => void;
  goToQuestion: (index: number) => void;
  setTimeRemaining: (seconds: number) => void;
  setSubmitting: (submitting: boolean) => void;
  getAnswers: () => SubmitAnswerRequest[];
  getAnsweredCount: () => number;
  reset: () => void;
}

export const useExamStore = create<ExamState>((set, get) => ({
  currentExam: null,
  currentAttemptId: null,
  currentQuestionIndex: 0,
  answers: new Map(),
  timeRemaining: 0,
  isSubmitting: false,

  startExam: (exam, attemptId) => {
    set({
      currentExam: exam,
      currentAttemptId: attemptId,
      currentQuestionIndex: 0,
      answers: new Map(),
      timeRemaining: exam.time_limit_minutes * 60,
      isSubmitting: false,
    });
  },

  answerQuestion: (questionId, answerId) => {
    const newAnswers = new Map(get().answers);
    newAnswers.set(questionId, answerId);
    set({ answers: newAnswers });
  },

  nextQuestion: () => {
    const { currentQuestionIndex, currentExam } = get();
    if (currentExam && currentQuestionIndex < currentExam.questions.length - 1) {
      set({ currentQuestionIndex: currentQuestionIndex + 1 });
    }
  },

  prevQuestion: () => {
    const { currentQuestionIndex } = get();
    if (currentQuestionIndex > 0) {
      set({ currentQuestionIndex: currentQuestionIndex - 1 });
    }
  },

  goToQuestion: (index) => {
    const { currentExam } = get();
    if (currentExam && index >= 0 && index < currentExam.questions.length) {
      set({ currentQuestionIndex: index });
    }
  },

  setTimeRemaining: (seconds) => {
    set({ timeRemaining: Math.max(0, seconds) });
  },

  setSubmitting: (submitting) => {
    set({ isSubmitting: submitting });
  },

  getAnswers: () => {
    const answers: SubmitAnswerRequest[] = [];
    get().answers.forEach((answerId, questionId) => {
      answers.push({ question_id: questionId, selected_answer_id: answerId });
    });
    return answers;
  },

  getAnsweredCount: () => {
    return get().answers.size;
  },

  reset: () => {
    set({
      currentExam: null,
      currentAttemptId: null,
      currentQuestionIndex: 0,
      answers: new Map(),
      timeRemaining: 0,
      isSubmitting: false,
    });
  },
}));
